import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { View, Pressable, Platform, RefreshControl, useWindowDimensions, StyleSheet, FlatList, ActivityIndicator } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Title, Text } from '@constants/Themed';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import LottieView from '@utils/lottieAnimation';
import { Entypo, Ionicons, AntDesign, FontAwesome, FontAwesome5, SimpleLineIcons, MaterialIcons } from '@expo/vector-icons';
import apis from '@api/api';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import moment from 'moment';
import { XStack, YStack, Card as TamaguiCard, Button, SizableText, Tabs } from 'tamagui';
import { filter, uniqueId, debounce } from 'lodash';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import webTransition from '@utils/webTransition';
import useGlobalUser from '@hooks/getGlobalUser';
import DataCounter from '../components/DataCounter';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import getDaysAgo from '@utils/getDaysAgo';
import getCurrencySymbol from '@hooks/getCurrency';
import { useCanProcessStripePayments } from "@hooks/useStripeConnection";
import { Pagination } from '@components/Global/Pagination/Pagination';

import PublicDataItem from './components/PublicDataItem';
import AddBountyModal from './components/AddBountyModal';
import FilterModal from './components/FilterModal';
import RenderDataItems from '../components/DataItem/DataItem';
import { Loading } from '@components/Global/Loading';

const PublicDataScreen = ({ navigation }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('repo');
  const [searchState, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [listHorizontal, setListHorizontal] = useState(false);
  const [activeState, setActiveState] = useState("repo");
  const [sortBy, setSortBy] = useState('popularity');
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const theme = themeSelector();
  const selectedId = useGlobalUser();
  const addBountyRef = useRef(null);
  const filterRef = useRef(null);
  const stripeConnected = useCanProcessStripePayments();

  // Debounce search input
  const debouncedSearchUpdate = useMemo(
    () => debounce((search) => setDebouncedSearch(search), 500),
    []
  );

  useEffect(() => {
    debouncedSearchUpdate(searchState);
    return () => {
      debouncedSearchUpdate.cancel();
    };
  }, [searchState, debouncedSearchUpdate]);

  const sortOptions = [
    { value: 'popularity', label: 'Popularity', icon: <FontAwesome name="fire" size={16} color="white" /> },
    { value: 'newest', label: 'Newest', icon: <FontAwesome name="clock-o" size={16} color="white" /> },
    { value: 'oldest', label: 'Oldest', icon: <FontAwesome name="history" size={16} color="white" /> }
  ];

  const textHelp = useMemo(() => [
    'Discover datasets shared by users worldwide',
    'Contribute to global knowledge by sharing your own datasets',
    'Find valuable data bounties from verified organizations',
    'Monetize your datasets and earn from your contributions',
    'Access high-quality public datasets for your projects'
  ], []);

  interface PaginationData {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  }

  interface ApiResponse {
    data: any[];
    pagination: PaginationData | null;
  }

  // Reset to page 1 when filters, search, sort, or active state changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeState, sortBy, debouncedSearch, filters]);

  const { 
    data: publicDataResponse,
    status,
    error,
    isLoading,
    isFetching,
    refetch 
  } = useQuery<ApiResponse>({
    queryKey: ['publicDataSet', activeState, sortBy, debouncedSearch, filters, currentPage, pageSize],
    queryFn: async () => {
      console.log('PublicDataScreen: Fetching data for state:', activeState, 'page:', currentPage, 'pageSize:', pageSize);
      
      if (activeState === 'bounty') {
        const data = await apis.getBounties();
        return { data: data?.data || [], pagination: null };
      } else {
        const result = await apis.getPublicData(sortBy, currentPage, pageSize, debouncedSearch, filters);
        return {
          data: result?.data || [],
          pagination: result?.pagination || null
        };
      }
    },
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  // Extract data and pagination from response
  const publicData = useMemo(() => {
    return publicDataResponse?.data || [];
  }, [publicDataResponse]);

  const pagination = useMemo(() => {
    return publicDataResponse?.pagination || null;
  }, [publicDataResponse]);

  const processedData = useMemo(() => {
    if (!publicData?.length) return [];

    // Only filter client-side for bounties as public data is filtered server-side
    if (activeState === 'bounty') {
      if (!searchState) return publicData;
      const formattedQuery = searchState.toLowerCase().replace(/ /g, '_');
      return filter(publicData, (item) =>
        item.dataTitle?.toLowerCase().includes(formattedQuery) ||
        item.title?.toLowerCase().includes(formattedQuery) ||
        item.description?.toLowerCase().includes(formattedQuery) ||
        item.dataTags?.includes(formattedQuery)
      );
    }

    return publicData;
  }, [searchState, publicData, activeState]);

  // Calculate layout based on windowWidth for web, or containerWidth for native
  useEffect(() => {
    const effectiveWidth = Platform.OS === 'web' ? windowWidth : containerWidth;
    if (effectiveWidth > 0) {
    }
  }, [windowWidth, containerWidth]);

  const onLayout = useCallback((event) => {
    const { width } = event;
    try {
      if (width > 0 && width !== containerWidth) {
        setContainerWidth(width);
      }
    } catch (e) {
      console.error("Error setting container width:", e);
    }
  }, [containerWidth]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch().finally(() => setRefreshing(false));
  }, [refetch]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    if (Platform.OS === 'web') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);

  // Memoize search callback to prevent input focus loss
  const handleSearchCallback = useCallback((text) => {
    setSearchState(text);
  }, []);

  // Memoize custom icon functions to prevent unnecessary re-renders
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleToggleHorizontal = useCallback(() => {
    setListHorizontal(prev => !prev);
  }, []);

  const handleNavigateToCapture = useCallback(() => {
    navigation.navigate('CaptureScreen');
  }, [navigation]);

  const handleOpenFilters = useCallback(() => {
    filterRef.current?.openModal();
  }, []);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Memoize tab change handler to prevent unnecessary re-renders
  const handleTabChange = useCallback((value) => {
    setActiveState(value);
    refetch();
  }, [refetch]);

  const renderFlatListItem = useCallback(({ item, index }) => {
    if (!item) return null;

    // Calculate item width properly accounting for margins and container constraints
    const itemMargin = 5;
    const itemMarginTotal = itemMargin * 2; // 5px on each side
    // Account for container padding (4% on each side = 8% total)
    const containerPadding = Platform.OS === 'web' && containerWidth > 0
      ? containerWidth * 0.08
      : 0;
    // Reduce item width further (90% of available width)
    const itemWidthMultiplier = 0.9;

    // Calculate item width constraints
    const itemWidth = Platform.OS === 'web' && containerWidth > 0
      ? (listHorizontal
        ? containerWidth
        : Math.max(0, (containerWidth - itemMarginTotal - containerPadding) * itemWidthMultiplier))
      : undefined;

    const itemStyle: any = {
      margin: itemMargin,
      ...(Platform.OS === 'web' ? {
        // Use calculated width if available, otherwise use percentage
        width: itemWidth || '100%',
        maxWidth: itemWidth || '100%',
        minWidth: 0,
        // Ensure box-sizing includes padding and borders
        boxSizing: 'border-box' as const,
        overflow: 'hidden' as const,
        // Prevent flex expansion
        flexShrink: 1,
        flexGrow: 0,
      } : {
        flex: 1,
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          flexShrink: 1,
      })
    };

    if (activeState === 'bounty') {
      return (
        <View style={[itemStyle, { overflow: 'hidden' }]} key={item._id || `bounty-${index}`}>
          <TamaguiCard
            padding={20}
            borderRadius={16}
            backgroundColor={theme ? "#ffffff" : "#1e293b"}
            shadowColor="#000"
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.1}
            shadowRadius={8}
            elevation={5}
            borderWidth={1}
            borderColor={theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}
            width="100%"
            maxWidth="100%"
            {...(Platform.OS === 'web' && {
              style: {
                boxSizing: 'border-box',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
              }
            })}
          >
            <View style={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: item.status === 'OPEN' ? '#10b981' : '#6b7280',
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 8,
              zIndex: 1
            }}>
              <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                {item.status || "OPEN"}
              </Text>
            </View>

            <YStack gap={16} maxWidth="100%" overflow="hidden">
              <YStack gap={8} maxWidth="100%" overflow="hidden">
                <Text style={{
                  fontSize: 24,
                  fontWeight: '700',
                  color: '#10b981'
                }}>
                  {getCurrencySymbol("USD")} {item.offer?.toFixed(2) || '0.00'}
                </Text>
                <Text style={{
                  fontSize: 18,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  flexShrink: 1,
                  flexWrap: 'wrap'
                }} numberOfLines={2}>
                  {item.title}
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  lineHeight: 20,
                  flexShrink: 1,
                  flexWrap: 'wrap'
                }} numberOfLines={3}>
                  {item.description}
                </Text>
              </YStack>

              <XStack justifyContent="space-between" alignItems="center" maxWidth="100%" overflow="hidden">
                <YStack gap={4} flexShrink={1} maxWidth="100%">
                  <Text style={{ fontSize: 12, color: theme ? '#64748b' : '#94a3b8' }} numberOfLines={1}>
                    Created: {moment(item.createdAt).format("DD/MM/YYYY")}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme ? '#64748b' : '#94a3b8' }} numberOfLines={1}>
                    Updated: {getDaysAgo(item.updatedAt)}
                  </Text>
                </YStack>

                <XStack gap={12} flexShrink={0}>
                  <Pressable style={{
                    backgroundColor: '#3b82f6',
                    padding: 8,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AntDesign name="edit" size={16} color="white" />
                  </Pressable>
                  <Pressable style={{
                    backgroundColor: '#f59e0b',
                    padding: 8,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Entypo name="bookmark" size={16} color="white" />
                  </Pressable>
                </XStack>
              </XStack>
            </YStack>
          </TamaguiCard>
        </View>
      );
    }

    // Calculate available width for RenderDataItems, accounting for margins and padding
    const availableWidthForItem = Platform.OS === 'web' && containerWidth > 0
      ? (listHorizontal ? containerWidth : Math.max(0, (containerWidth - itemMarginTotal - containerPadding) * itemWidthMultiplier))
      : containerWidth;

    // Calculate max width for the item to prevent expansion
    const itemMaxWidth = "100%"

    return (
      <View
        style={[
          itemStyle,
          {
            overflow: 'hidden',
            ...(Platform.OS === 'web' && itemMaxWidth && {
              width: itemMaxWidth,
              maxWidth: itemMaxWidth,
              minWidth: 0,
              boxSizing: 'border-box',
            })
          }
        ]}
        key={item._id || `public-${index}`}
      >
        <RenderDataItems
          availableWidth={availableWidthForItem}
          handleRefresh={refetch}
          item={item}
          compactMode
          index={index}
          listHorizontal={listHorizontal}
        />
      </View>
    );
  }, [processedData, containerWidth, listHorizontal, refetch, activeState, theme]);

  const keyExtractor = useCallback((item, index) => {
    return item?._id || `item-${index}`;
  }, []);

  const ListEmptyComponent = useMemo(() => () => (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 30, gap: 30, marginTop: 100 }}>
      {searchState ? (
        <View style={[styles.noteContainer, { padding: 20, alignItems: 'center' }]}>
          <LottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ width: '80%', height: 200 }} />
          <Text>No {activeState === 'bounty' ? 'bounties' : 'public datasets'} found matching your query.</Text>
        </View>
      ) : (
        <View style={{ gap: 30, flex: 1, maxWidth: "100%", alignSelf: "stretch" }}>
          <TamaguiCard padding={40} borderRadius={20} top={-300} backgroundColor={theme ? "#f8fafc" : "#1e293b"}>
            <YStack alignItems="center" gap={20}>
              <View style={{
                width: 80,
                height: 80,
                backgroundColor: activeState === 'bounty' ? '#8b5cf6' : '#3b82f6',
                borderRadius: 20,
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {activeState === 'bounty' ? (
                  <Ionicons name="pricetag" size={40} color="white" />
                ) : (
                  <FontAwesome name="globe" size={40} color="white" />
                )}
              </View>
              <Text style={{ fontSize: 24, color: theme ? "black" : "white", fontWeight: '700', textAlign: 'center' }}>
                {activeState === 'bounty' ? 'No Bounties Available' : 'No Public Data Available'}
              </Text>
              <Text style={{ fontSize: 16, opacity: 0.7, textAlign: 'center', maxWidth: 300 }}>
                {activeState === 'bounty'
                  ? 'No data bounties are currently available. Check back later or create your own bounty.'
                  : 'No public datasets are currently available. Be the first to share your data with the community!'
                }
              </Text>
              <Pressable
                style={{
                  backgroundColor: activeState === 'bounty' ? '#8b5cf6' : '#3b82f6',
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 12,
                  marginTop: 10
                }}
                onPress={() => {
                  if (activeState === 'bounty') {
                    addBountyRef.current?.openModal();
                  } else {
                    navigation.navigate('CaptureScreen');
                  }
                }}
              >
                <Text style={{ color: 'white', fontWeight: '600' }}>
                  {activeState === 'bounty' ? 'Create Bounty' : 'Share Your Data'}
                </Text>
              </Pressable>
            </YStack>
          </TamaguiCard>
        </View>
      )}
    </View>
  ), [searchState, theme, navigation, activeState, addBountyRef]);

  const renderFooter = useCallback(() => {
    return null; // Pagination controls are now handled separately
  }, []);

  const HorizontalList = () => {
    return (
      <FlatList
        data={processedData}
        renderItem={renderFlatListItem}
        keyExtractor={keyExtractor}
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        ListEmptyComponent={ListEmptyComponent}
        ListFooterComponent={renderFooter}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        style={{
          flex: 1,
          width: '100%',
          maxWidth: '100%',
          ...(Platform.OS === 'web' && {
            boxSizing: 'border-box',
          })
        }}
        contentContainerStyle={{
          ...(Platform.OS === 'web' && {
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          })
        }}
      />
    );
  }

  return (
    <DefaultScreen 
      layoutCallback={onLayout}
      scrollDisabled
      screenTitle={activeState === 'bounty' ? 'Data Bounties' : 'Public Data'}
      backButton
      noMargin
      containerStyle={{
        flex: 1,
        paddingBottom: 40,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        ...(Platform.OS === 'web' && {
          boxSizing: 'border-box',
        })
      }}
      robotHelperProps={{
        textHelp,
        showByDefault: true,
        autoShow: true,
        position: 'bottom-left',
      }}
      searchBar={{
        callback: handleSearchCallback,
        searchState: searchState,
        extraComponents: (
          <View style={{ left: 30, top: 3, flexDirection: "row", gap: 10 }}>
            <DataCounter
              extraCount={processedData?.length || 0}
              dataCount={processedData?.length || 0}
              refreshCallback={refetch}
              props={{ style: { maxWidth: 150, top: -5, left: 5 } }}
            />
          </View>
        ),
        customIcons: [
          { function: handleRefresh, icon: <FontAwesome name="refresh" size={18} color={"white"} />, functionName: 'refresh' },
          { function: handleToggleHorizontal, icon: listHorizontal ? <FontAwesome5 name="th-list" size={18} color={"white"} /> : <SimpleLineIcons name="grid" size={18} color={"white"} />, functionName: 'changeView' },
          { function: handleNavigateToCapture, icon: <MaterialIcons name="folder" size={18} color="white" />, functionName: 'myData' },
          { function: handleOpenFilters, icon: <FontAwesome name="filter" size={18} color={"white"} />, functionName: 'filter' }
        ]
      }}
    >
      <AddBountyModal ref={addBountyRef} onBountyCreated={refetch} />
      <FilterModal ref={filterRef} onApplyFilters={handleApplyFilters} currentFilters={filters} />

      {!stripeConnected && activeState === 'bounty' && (
        <Animatable.View
          animation="fadeInDown"
          style={{
            backgroundColor: theme ? "#fef3c7" : "#451a03",
            borderRadius: 8,
            padding: 12,
            marginHorizontal: 20,
            marginTop: 10,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10
          }}
        >
          <MaterialIcons name="warning" size={20} color={theme ? "#9a3412" : "#fca5a1"} />
          <Text style={{
            color: theme ? "#9a3412" : "#fca5a1",
            fontSize: 12,
            fontWeight: '600',
            flex: 1
          }}>
            Stripe is not connected. Please connect your Stripe account in settings to receive bounty payments.
          </Text>
          <Pressable
            onPress={() => navigation.navigate("Integrations")}
            style={{
              backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 6
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: 'bold', color: theme ? "#9a3412" : "#fca5a1" }}>Connect</Text>
          </Pressable>
        </Animatable.View>
      )}

      {/* Tabs for switching between Public Data and Bounties */}
      <Tabs
        orientation="horizontal"
        borderBlockColor={'$colorTransparent'}
        borderWidth={0}
        flex={1}
        value={activeState}
        maxHeight={70}
        paddingHorizontal={20}
        defaultValue='repo'
        onValueChange={handleTabChange}
        minWidth={"100%"}
        flexDirection="column"
      >
        <Tabs.List
          paddingHorizontal={0}
          backgroundColor="transparent"
          minWidth="100%"
          top={30}
          alignSelf="stretch"
        >
          <Tabs.Tab
            value={"repo"}
            flex={1}
            paddingVertical={10}
            paddingHorizontal={16}
            gap="$2"
            backgroundColor={theme ? '#f0f4ff' : '#1e1e1e'}
          >
            <FontAwesome name="globe" size={20} color={theme ? "black" : "white"} />
            <SizableText>
              Public Data
            </SizableText>
          </Tabs.Tab>
          <Tabs.Tab
            value={"bounty"}
            flex={1}
            paddingVertical={10}
            paddingHorizontal={16}
            gap="$2"
            backgroundColor={theme ? '#f0f4ff' : '#1e1e1e'}
          >
            <Ionicons name="pricetag" size={20} color={theme ? "black" : "white"} />
            <SizableText>
              Bounties
            </SizableText>
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {isLoading ? (
        <View style={{ flex: 1, top: -100, alignItems: 'center', justifyContent: "center" }}>
          <Loading />
        </View>
      ) : (
          <View style={{
            marginTop: 20,
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            ...(Platform.OS === 'web' && {
              boxSizing: 'border-box',
            })
          }}>
          {listHorizontal ? (
              <HorizontalList />
          ) : (
                <FlatList
              data={processedData}
                  renderItem={renderFlatListItem}
                  keyExtractor={keyExtractor}
                  horizontal={false}
                  showsVerticalScrollIndicator={false}
                  ListEmptyComponent={ListEmptyComponent}
                  ListFooterComponent={renderFooter}
                  refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                  }
                  style={{
                    flex: 1,
                    width: '100%',
                    maxWidth: '100%',
                    ...(Platform.OS === 'web' && {
                      boxSizing: 'border-box',
                    })
                  }}
                  contentContainerStyle={{
                    paddingHorizontal: Platform.OS === 'web' ? "4%" : 10,
                    ...(Platform.OS === 'web' && {
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    })
              }}
            />
            )}
          </View>
      )}

      {/* Pagination Controls */}
      {activeState !== 'bounty' && pagination && pagination.totalPages > 0 && (
        <View style={{
          paddingHorizontal: 20,
          paddingVertical: 16,
          width: '100%',
          ...(Platform.OS === 'web' && {
            boxSizing: 'border-box',
          })
        }}>
          <Pagination
            pagination={{
              currentPage: pagination.currentPage || 1,
              totalPages: pagination.totalPages || 1,
              totalRecords: pagination.totalCount || 0,
              hasNextPage: pagination.hasNextPage || false,
              hasPrevPage: pagination.hasPrevPage || false,
              nextPage: pagination.nextPage || undefined,
              prevPage: pagination.prevPage || undefined
            }}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            showPageSizeSelector={true}
            showTotalRecords={true}
            compact={false}
            style={{ width: '100%' }}
          />
        </View>
      )}

      {/* Floating Add Bounty Button */}
      {activeState === 'bounty' && (
        <Animatable.View
          useNativeDriver
          animation={'slideInUp'}
          style={{
            position: "absolute",
            bottom: 30,
            right: 30,
            zIndex: 10
          }}
        >
          <Pressable
            style={{
              backgroundColor: '#8b5cf6',
              paddingHorizontal: 20,
              paddingVertical: 12,
              borderRadius: 25,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8
            }}
            onPress={() => addBountyRef.current?.openModal()}
          >
            <AntDesign name="plus-circle" size={20} color="white" />
            <Text style={{ color: 'white', fontWeight: '600' }}>Add Bounty</Text>
          </Pressable>
        </Animatable.View>
      )}
    </DefaultScreen>
  );
};
export default PublicDataScreen;